import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { baseStyles } from './styles/shared.js';
import { ModelerController } from './controllers/modeler-controller.js';
import { DocumentController } from './controllers/document-controller.js';
import { ChatbotController } from './controllers/chatbot-controller.js';
import { IngestionController } from './controllers/ingestion-controller.js';
import { LayoutController } from './controllers/layout-controller.js';
import type { SysmlApiAdapter } from './api/api-adapter.js';
import { SysmlApi } from './api/sysml-api.js';
import type { SysmlApiConfig } from './api/types.js';
import { emitReady, SYSML_HOST_EVENTS } from './utils/events.js';

// Import child components
import './components/sysml-workspace.js';
import './components/sysml-domain-loader.js';

// Register Stencil web components (lazy loader)
import { defineCustomElements } from '@arctek/sysml-studio/loader';
defineCustomElements(window);

// ---------------------------------------------------------------------------
// <sysml-mfe> — Root custom element
//
// Ported from: PlatformSysmlWorkspace.vue
// MFE host attributes: api-base-url, auth-mode, auth-token
// ---------------------------------------------------------------------------

/**
 * SysML v2 Modeling micro-frontend.
 *
 * @fires sysml-ready - Emitted when the MFE is mounted and ready
 * @fires sysml-status - Status updates (success/error/warn/info)
 * @fires sysml-document-saved - Document persistence events
 * @fires sysml-document-changed - Active document changed
 * @fires sysml-parse-complete - Parse pipeline completed
 * @fires sysml-error - Error events
 *
 * @attr api-base-url - Base URL for the SysML API
 * @attr auth-mode - Authentication mode: 'cookie' or 'bearer'
 * @attr auth-token - Bearer token (when auth-mode is 'bearer')
 */
@customElement('sysml-mfe')
export class SysmlMfe extends LitElement {
  static override styles = [
    baseStyles,
    css`
      :host {
        display: block;
        width: 100%;
        height: 100%;
        overflow: hidden;

        /* Tooltip: always high-contrast dark bg + light text regardless of theme */
        --r5-tooltip-bg: hsl(222.2 84% 4.9%);
        --r5-tooltip-color: hsl(210 40% 98%);

        /* ── Light theme: Slate/Neutral + Indigo accent ─────────────── */
        --r5-bg: hsl(210 40% 98%);             /* slate-50 */
        --r5-surface: hsl(0 0% 100%);           /* white */
        --r5-surface-muted: hsl(210 40% 96.1%); /* slate-100 */
        --r5-surface-elevated: hsl(0 0% 100%);  /* white */
        --r5-text: hsl(222.2 47.4% 11.2%);      /* slate-900 */
        --r5-text-muted: hsl(215.4 16.3% 46.9%);/* slate-500 */
        --r5-border: hsl(214.3 31.8% 91.4%);    /* slate-200 */
        --r5-primary: hsl(238.7 83.5% 66.7%);   /* indigo-500 */
        --r5-primary-foreground: hsl(0 0% 100%);
      }

      /* ── Dark theme: Slate + Indigo accent ──────────────────────── */
      :host(.dark),
      :host([data-theme="dark"]) {
        --r5-bg: hsl(222.2 84% 4.9%);           /* slate-950 */
        --r5-surface: hsl(217.2 32.6% 17.5%);   /* slate-800 */
        --r5-surface-muted: hsl(215.3 25% 26.7%);/* slate-700 */
        --r5-surface-elevated: hsl(217.2 32.6% 17.5%); /* slate-800 */
        --r5-text: hsl(210 40% 98%);             /* slate-50 */
        --r5-text-muted: hsl(215 20.2% 65.1%);  /* slate-400 */
        --r5-border: hsl(217.2 32.6% 17.5%);    /* slate-800 */
        --r5-primary: hsl(238.7 83.5% 66.7%);   /* indigo-500 */
        --r5-primary-foreground: hsl(0 0% 100%);
      }
    `,
  ];

  // ── Host attributes ──────────────────────────────────────────────────────

  @property({ attribute: 'api-base-url' })
  apiBaseUrl = '';

  @property({ attribute: 'auth-mode' })
  authMode: 'cookie' | 'bearer' = 'cookie';

  @property({ attribute: 'auth-token' })
  authToken?: string;

  // ── Controllers ──────────────────────────────────────────────────────────

  private modeler = new ModelerController(this);
  private documents = new DocumentController(this);
  private chatbot = new ChatbotController(this);
  private ingestion = new IngestionController(this);
  private layout = new LayoutController(this);

  // ── Lifecycle ────────────────────────────────────────────────────────────

  override connectedCallback(): void {
    super.connectedCallback();

    const api = this._createApi();
    this.documents.initialize(api);
    this.chatbot.initialize(api);

    // Wire ingestion controller to the current model context so AI
    // extraction and generation are aware of existing elements.
    this.ingestion.modelContextProvider = () => {
      const model = this.modeler.parsedModel;
      if (!model && !this.modeler.sourceText) return null;

      const elements = (model?.allElements ?? []).map((el) => ({
        name: el.name,
        type: el.elementType,
        id: el.id,
      }));

      const requirementNames = (model?.allRequirementDefs ?? []).map(
        (r) => r.name,
      );

      const packageNames = (model?.packages ?? []).map((p) => p.name);

      return {
        sourceText: this.modeler.sourceText,
        elements,
        requirementNames,
        packageNames,
      };
    };

    // Listen for host events
    this.addEventListener(SYSML_HOST_EVENTS.REFRESH, this._onRefresh);
    this.addEventListener(SYSML_HOST_EVENTS.THEME_CHANGE, this._onThemeChange);

    // Signal host: MFE is mounted
    emitReady(this);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener(SYSML_HOST_EVENTS.REFRESH, this._onRefresh);
    this.removeEventListener(SYSML_HOST_EVENTS.THEME_CHANGE, this._onThemeChange);
  }

  override updated(changedProps: Map<string, unknown>): void {
    super.updated(changedProps);

    // When documents finish loading, load the first document into the editor
    if (!this.documents.loading && this.documents.currentDocument && !this.modeler.sourceText) {
      this.modeler.loadSourceText(this.documents.currentDocument.content);
    }
  }

  override render() {
    if (this.documents.loading) {
      return html`<sysml-domain-loader></sysml-domain-loader>`;
    }

    return html`
      <sysml-workspace
        .modeler=${this.modeler}
        .documents=${this.documents}
        .chatbot=${this.chatbot}
        .ingestion=${this.ingestion}
        .layout=${this.layout}
        .layoutGeneration=${this.layout.generation}
        .generation=${this.modeler.generation}
        .ingestionGeneration=${this.ingestion.generation}
      ></sysml-workspace>
    `;
  }

  // ── Internal ─────────────────────────────────────────────────────────────

  private _createApi(): SysmlApiAdapter {
    const config: SysmlApiConfig = {
      baseUrl: this.apiBaseUrl,
      authMode: this.authMode,
      authToken: this.authToken,
    };
    return new SysmlApi(config);
  }

  private _onRefresh = (): void => {
    const api = this._createApi();
    this.documents.initialize(api);
  };

  private _onThemeChange = (_e: Event): void => {
    // Theme propagation handled by CSS custom properties
    this.requestUpdate();
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'sysml-mfe': SysmlMfe;
  }
}
